import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed, action } from 'mobx';
import TimelineAdminOverview, { TimelineGroup } from '../../component/TimelineAdminOverview';
import { User, UserStore } from '../../store/User';
import { DeveloperAllocation, DeveloperAllocationStore } from '../../store/planning';
import { Project, ProjectStore } from '../../store/Project';
import AllocationEditModal from './AllocationEditModal';
import { t } from '../../i18n';

const PALETTE = ['#e53e3e', '#38a169', '#3182ce', '#805ad5', '#dd6b20', '#319795', '#d53f8c', '#6b46c1'];

function userColor(u: any): string {
    const name = (u?.fullName || u?.firstName || '') as string;
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return PALETTE[Math.abs(h) % PALETTE.length];
}

@observer
export default class DeveloperOverview extends TimelineAdminOverview {
    myFilterKey = 'planning-developer-overview';
    title = t('nav.planning.developerOverview', 'Developer Overview');

    // Main store: users (developers)
    store = new UserStore({ limit: false });

    // Auxiliary stores
    developerAllocationStore = new DeveloperAllocationStore({ relations: ['entries.project', 'user'], limit: false });
    projectStore = new ProjectStore({ relations: ['company'], limit: false });

    // Modal state
    @observable allocModalOpen = false;
    @observable editingAllocation: DeveloperAllocation | null = null;

    async componentDidMount() {
        await this.fetch();
    }

    async fetch() {
        await Promise.all([
            this.store.fetch().catch((e: any) => console.warn('user fetch failed', e)),
            this.developerAllocationStore.fetch().catch((e: any) => console.warn('alloc fetch failed', e)),
            this.projectStore.fetch().catch((e: any) => console.warn('project fetch failed', e)),
        ]);
    }

    get users(): User[] {
        return this.store.models as User[] || [];
    }

    get allocations(): DeveloperAllocation[] {
        return this.developerAllocationStore.models as DeveloperAllocation[] || [];
    }

    get projects(): Project[] {
        return (this.projectStore.models as Project[] || []).filter(p => !p.hidden);
    }

    getDevProjects(userId: number) {
        return this.projects.filter(p => {
            return this.allocations.some(a => {
                const uId = (a as any).user?.id ?? (a as any).user;
                if (Number(uId) !== Number(userId)) return false;
                const entries = (a as any).entries?.models || (a as any).entries || [];
                return entries.some((e: any) => {
                    const epid = e.project?.id ?? e.project;
                    return Number(epid) === Number(p.id);
                });
            });
        });
    }

    getDevAllocations(userId: number) {
        return this.allocations.filter(a => {
            const uId = (a as any).user?.id ?? (a as any).user;
            return Number(uId) === Number(userId);
        });
    }

    @computed get timelineGroups(): TimelineGroup[] {
        return this.users.map(u => {
            const allocs = this.getDevAllocations(u.id);
            return {
                id: u.id,
                label: (u as any).fullName || ((u as any).firstName || '') + ' ' + ((u as any).lastName || '').trim(),
                items: allocs,
                color: userColor(u),
            };
        }).filter(g => g.items.length > 0);
    }

    getBarStart(item: any): Date | null {
        return item.startDate ? new Date(item.startDate) : null;
    }

    getBarEnd(item: any): Date | null {
        return item.endDate ? new Date(item.endDate) : null;
    }

    barColor(item: any): string {
        const projId = item.project?.id ?? item.project;
        const proj = this.projects.find(p => Number(p.id) === Number(projId));
        if (proj) {
            const name = proj.name || '';
            let h = 0;
            for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
            return PALETTE[Math.abs(h) % PALETTE.length];
        }
        return '#3182ce';
    }

    getBarLabel(item: any): string {
        const projId = item.project?.id ?? item.project;
        const proj = this.projects.find(p => Number(p.id) === Number(projId));
        return proj?.name || 'Unknown Project';
    }

    renderTimelineBar(item: any): React.ReactNode {
        const projId = item.project?.id ?? item.project;
        const proj = this.projects.find(p => Number(p.id) === Number(projId));
        const entries = (item as any).entries?.models || (item as any).entries || [];
        const dayCount = new Set(entries.map((e: any) => e.dayOfWeek)).size;
        return (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {proj?.name || 'Unknown'}
                    {item.startDate && item.endDate ? ' \u00B7 ' + new Date(item.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' \u2013 ' + new Date(item.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                    {dayCount > 0 ? ' \u00B7 ' + dayCount + 'd/wk' : ''}
                </span>
                <button
                    data-test-edit-allocation={item.id}
                    onClick={(e) => { e.stopPropagation(); this.openAllocModal(item); }}
                    style={{
                        flexShrink: 0, background: 'rgba(255,255,255,0.85)',
                        border: '1px solid rgba(0,0,0,0.15)', borderRadius: 3,
                        padding: '0 4px', fontSize: 11, cursor: 'pointer', color: '#333',
                        lineHeight: '18px',
                    }}
                >
                    {'\u270E'}
                </button>
            </span>
        );
    }

    @action openAllocModal = (alloc: DeveloperAllocation) => {
        this.editingAllocation = alloc;
        this.allocModalOpen = true;
    };

    @action closeAllocModal = () => {
        this.allocModalOpen = false;
        this.editingAllocation = null;
    };

    @action afterAllocSave = async () => {
        this.closeAllocModal();
        await this.developerAllocationStore.fetch();
    };

    renderEditModal(): React.ReactNode {
        return (
            this.allocModalOpen && this.editingAllocation ? (
                <AllocationEditModal
                    model={this.editingAllocation}
                    open={this.allocModalOpen}
                    onClose={this.closeAllocModal}
                    afterSave={this.afterAllocSave}
                    allocations={this.developerAllocationStore.models}
                />
            ) : null
        );
    }
}
